/** Detail zakázky — pixel-perfect 1:1 z prototype/detail.jsx DetailMain.
 *  Header + 4 tabs (Übersicht / Fotos / Dokumente / Reklamationen). */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Ic } from "@/components/ui/icons";
import { StatusBadge } from "@/components/ui/badge";
import { Empty } from "@/components/ui/empty";
import {
  enrichOrdersWithEndClient,
  fetchDealComplaints,
  fetchDealDocuments,
  fetchDealPhotos,
  fetchOrderById,
} from "@/lib/fetchers";
import { fmtWeekday } from "@/lib/format";
import { orderStatusMeta } from "@/lib/status";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";
import { OverviewTab } from "./tabs/overview";
import { PhotosTab } from "./tabs/photos";
import { DocumentsTab } from "./tabs/documents";
import { ComplaintsTab } from "./tabs/complaints";
import { ReklamationModal } from "./reklamation-modal";

type TabId = "overview" | "photos" | "docs" | "complaints";

export function OrderDetailRoute() {
  const { orderNr } = useParams<{ orderNr: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>("overview");
  const [reklamationOpen, setReklamationOpen] = useState(false);

  const orderQ = useQuery({
    queryKey: queryKeys.orderDetail(orderNr || "x"),
    queryFn: async () => {
      if (!orderNr) return null;
      const o = await fetchOrderById(orderNr);
      if (!o) return null;
      const [enriched] = await enrichOrdersWithEndClient([o]);
      return enriched ?? o;
    },
    enabled: !!orderNr,
  });

  const photosQ = useQuery({
    queryKey: queryKeys.orderPhotos(orderNr || "x"),
    queryFn: () => fetchDealPhotos(orderNr!),
    enabled: !!orderNr && tab === "photos",
  });

  const docsQ = useQuery({
    queryKey: queryKeys.orderDocs(orderNr || "x"),
    queryFn: () => fetchDealDocuments(orderNr!),
    enabled: !!orderNr && tab === "docs",
  });

  const complaintsQ = useQuery({
    queryKey: queryKeys.orderComplaints(orderNr || "x"),
    queryFn: () => fetchDealComplaints(orderNr!),
    enabled: !!orderNr && (tab === "complaints" || tab === "overview"),
  });

  if (!orderNr) {
    return <div style={{ padding: 60, color: "var(--text-3)" }}>{t.notFound}</div>;
  }

  const order = orderQ.data;
  if (orderQ.isLoading) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "var(--text-3)" }}>{t.loading}</div>
    );
  }
  if (!order) {
    return (
      <div style={{ padding: "32px 36px", maxWidth: 1280, margin: "0 auto" }}>
        <Empty title={t.notFound} />
      </div>
    );
  }

  const dateObj = order.scheduled_date ? new Date(order.scheduled_date) : null;
  const photoCount = photosQ.data?.length ?? 0;
  const docCount = docsQ.data?.length ?? 0;
  const complaintCount = complaintsQ.data?.length ?? 0;

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: "overview", label: t.detail.tabs.overview },
    { id: "photos", label: t.detail.tabs.photos, count: photoCount },
    { id: "docs", label: t.detail.tabs.docs, count: docCount },
    { id: "complaints", label: t.detail.tabs.complaints, count: complaintCount },
  ];

  return (
    <div style={{ padding: "24px 36px", maxWidth: 1280, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 18,
          fontSize: 12.5,
          color: "var(--text-3)",
        }}
      >
        <Link to="/orders" className="link" style={{ color: "var(--text-2)" }}>
          {t.detail.breadcrumb}
        </Link>
        <Ic.Chevron size={12} />
        <span style={{ color: "var(--text-2)" }} className="num">
          {order.title || order.id.slice(0, 8)}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 24,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 8,
            }}
          >
            <span
              className="num"
              style={{
                fontSize: 12.5,
                color: "var(--text-3)",
                letterSpacing: "0.04em",
              }}
            >
              {order.title || order.id.slice(0, 8)}
            </span>
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: 999,
                background: "var(--text-4)",
              }}
            />
            <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>
              {dateObj
                ? `${fmtWeekday(dateObj)}, ${dateObj.getDate()}.${dateObj.getMonth() + 1}.`
                : "—"}
            </span>
          </div>
          <h1 className="display" style={{ margin: 0, fontSize: 30 }}>
            {order.end_client_label || order.title || "—"}
            {order.installation_city ? ` · ${order.installation_city}` : ""}
          </h1>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <StatusBadge meta={orderStatusMeta(order.status_label)} />
            {order.system_type && <span className="badge">{order.system_type}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={() => setReklamationOpen(true)}>
            <Ic.Alert size={14} /> {t.detail.actions.complaint}
          </Button>
          <Button variant="secondary">
            <Ic.File size={14} /> {t.detail.actions.openAB}
          </Button>
        </div>
      </div>

      <div
        className="no-scrollbar"
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--border)",
          marginBottom: 24,
          overflowX: "auto",
        }}
      >
        {tabs.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            onClick={() => setTab(tabItem.id)}
            style={{
              padding: "12px 14px",
              background: "transparent",
              border: 0,
              color: tab === tabItem.id ? "var(--text)" : "var(--text-2)",
              fontSize: 13.5,
              fontWeight: 500,
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {tabItem.label}
            {tabItem.count !== undefined && tabItem.count > 0 && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--text-3)",
                  background: "var(--bg-elevated)",
                  padding: "1px 7px",
                  borderRadius: 999,
                }}
              >
                {tabItem.count}
              </span>
            )}
            {tab === tabItem.id && (
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: -1,
                  height: 2,
                  background: "var(--gold)",
                }}
              />
            )}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab order={order} complaints={complaintsQ.data || []} />}
      {tab === "photos" && <PhotosTab photos={photosQ.data || []} loading={photosQ.isLoading} />}
      {tab === "docs" && <DocumentsTab docs={docsQ.data || []} loading={docsQ.isLoading} />}
      {tab === "complaints" && (
        <ComplaintsTab
          complaints={complaintsQ.data || []}
          loading={complaintsQ.isLoading}
          onNew={() => setReklamationOpen(true)}
        />
      )}

      <ReklamationModal
        open={reklamationOpen}
        onOpenChange={setReklamationOpen}
        order={order}
        onSuccess={() => {
          setReklamationOpen(false);
          void complaintsQ.refetch();
          setTab("complaints");
        }}
      />

      <button type="button" onClick={() => navigate(-1)} style={{ display: "none" }} aria-hidden />
    </div>
  );
}
